import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatDistanceToNow, isValid } from "date-fns";
import { Check, Copy } from "lucide-react";
import type { CortexEventLogRow } from "@flowstate/types";
import { cn } from "../lib/cn";
import { copyEventToClipboard } from "../lib/copy-event";
import { getDomainRoot } from "../lib/domain";

// ── Domain badge colours ──────────────────────────────────────────────────

const DOMAIN_COLOURS: Record<string, string> = {
  ai: "bg-violet-500/15 text-violet-400 ring-violet-500/30",
  cortex: "bg-indigo-500/15 text-indigo-400 ring-indigo-500/30",
  task: "bg-blue-500/15 text-blue-400 ring-blue-500/30",
  notification: "bg-sky-500/15 text-sky-400 ring-sky-500/30",
  gamification: "bg-amber-500/15 text-amber-400 ring-amber-500/30",
  accounting: "bg-emerald-500/15 text-emerald-400 ring-emerald-500/30",
  webhook: "bg-orange-500/15 text-orange-400 ring-orange-500/30",
  system: "bg-slate-500/15 text-slate-400 ring-slate-500/30",
  memory: "bg-pink-500/15 text-pink-400 ring-pink-500/30",
  box: "bg-teal-500/15 text-teal-400 ring-teal-500/30",
  notebook: "bg-cyan-500/15 text-cyan-400 ring-cyan-500/30",
};

const STATUS_ICONS: Record<string, string> = {
  started: "●",
  succeeded: "✓",
  failed: "✗",
  info: "ℹ",
};

const STATUS_COLOURS: Record<string, string> = {
  started: "text-blue-400",
  succeeded: "text-green-400",
  failed: "text-red-400",
  info: "text-muted-foreground",
};

// ── Component ─────────────────────────────────────────────────────────────

interface EventRowProps {
  event: CortexEventLogRow;
  onClick?: (event: CortexEventLogRow) => void;
  isSelected?: boolean;
}

export function EventRow({ event, onClick, isSelected }: EventRowProps) {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const ok = await copyEventToClipboard(event);
      if (!ok) return;
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
    },
    [event],
  );

  const domainRoot = getDomainRoot(event.domain);
  const domainColour =
    DOMAIN_COLOURS[domainRoot] ??
    "bg-slate-500/15 text-slate-400 ring-slate-500/30";

  const statusColour = STATUS_COLOURS[event.status ?? "info"] ?? STATUS_COLOURS.info;
  const statusIcon = STATUS_ICONS[event.status ?? "info"] ?? STATUS_ICONS.info;

  const relTime = (() => {
    if (!event.occurred_at) return null;
    const date = new Date(event.occurred_at);
    return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : null;
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(event)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(event);
        }
      }}
      className={cn(
        "group w-full text-left px-3 py-2 flex items-start gap-2.5 border-b border-border/50",
        "hover:bg-muted/50 transition-colors text-sm cursor-pointer",
        isSelected && "bg-muted/70",
      )}
    >
      {/* Domain badge */}
      <span
        className={cn(
          "mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-mono ring-1",
          domainColour,
        )}
      >
        {domainRoot}
      </span>

      {/* Summary + type */}
      <div className="flex-1 min-w-0">
        <p className="truncate text-foreground leading-snug">
          {event.summary ?? event.event_type}
        </p>
        <p className="truncate text-[10px] text-muted-foreground font-mono mt-0.5">
          {event.event_type}
          {event.duration_ms != null && (
            <span className="ml-2 text-muted-foreground/70">
              {event.duration_ms}ms
            </span>
          )}
        </p>
      </div>

      {/* Copy + status + time */}
      <div className="shrink-0 flex items-start gap-1">
        <button
          type="button"
          onClick={handleCopy}
          onPointerDown={(e) => e.stopPropagation()}
          title={copied ? "Copied" : "Copy event JSON"}
          aria-label={copied ? "Copied event JSON" : "Copy event JSON"}
          className={cn(
            "h-7 w-7 min-h-[28px] min-w-[28px] rounded flex items-center justify-center",
            "text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
            "opacity-70 group-hover:opacity-100 focus-visible:opacity-100",
            copied && "text-green-400 opacity-100",
          )}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
        <div className="flex flex-col items-end gap-0.5 pt-0.5">
          <span className={cn("text-xs font-mono", statusColour)}>
            {statusIcon}
          </span>
          {relTime && (
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              {relTime}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
